/* eslint-disable @typescript-eslint/no-explicit-any */
import { UserProfile } from '@dynamic-labs/sdk-react-core';
import {
  useAuthenticatedKnockClient,
  useNotifications as useKnockNotifications,
} from '@knocklabs/react';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

const KNOCK_API_KEY = process.env.NEXT_PUBLIC_KNOCK_PUBLIC_API_KEY ?? '';
const KNOCK_FEED_CHANNEL_ID =
  process.env.NEXT_PUBLIC_KNOCK_FEED_CHANNEL_ID ?? '';

/**
 * Custom hook to manage notifications using Knock.
 * @function useNotifications
 * @param {UserProfile | undefined} user - The user profile.
 * @param {string} workflowKey - The workflow key for the notifications.
 * @param {'unread' | 'read' | 'unseen' | 'seen' | 'all'} [status] - The status of the notifications to fetch.
 * @param {boolean} [shouldRefetch] - Whether to refetch the notifications after marking as read.
 * @returns {object} An object containing notifications, loading state, refetch function, and markAsRead function.
 */
export const useNotifications = (
  user: UserProfile | undefined,
  workflowKey: string,
  status?: 'unread' | 'read' | 'unseen' | 'seen' | 'all',
  shouldRefetch?: boolean,
) => {
  const knockClient = useAuthenticatedKnockClient(
    KNOCK_API_KEY,
    user?.userId ?? '',
  );
  const feedClient = useKnockNotifications(knockClient, KNOCK_FEED_CHANNEL_ID, {
    source: workflowKey,
    ...(status && { status }),
  });

  /**
   * Fetches notifications from the Knock feed.
   * @function getNotifications
   * @returns {Promise<any[]>} The fetched notifications.
   */
  const getNotifications = useCallback(async () => {
    const response = await feedClient.fetch();
    return response?.data.entries;
  }, [feedClient]);

  const {
    data: notifications,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ['notifications', user?.userId as string, workflowKey, status],
    queryFn: getNotifications,
    enabled: !!user?.userId && !!workflowKey,
    refetchOnWindowFocus: false,
  });

  /**
   * Marks notifications as read based on a specific field.
   * @function markAsRead
   * @param {string} notificationId - The ID of the notification to mark as read.
   * @param {'nftId' | 'notificationId'} field - The field to match for marking as read.
   */
  const markAsRead = async (
    notificationId: string,
    field: 'nftId' | 'notificationId',
  ) => {
    const notificationsToRead = notifications?.filter(
      (notification: any) =>
        notification.data[field] && notification.data[field] === notificationId,
    );

    if (notificationsToRead) {
      await feedClient.markAsRead(notificationsToRead);
      shouldRefetch && refetch();
    }
  };

  return {
    notifications,
    isLoadingNotifications: isLoading,
    refetch,
    markAsRead,
  };
};
